import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { fetchMatch, fetchPrediction } from "../lib/api";
import PitchFormation from "../components/PitchFormation";
import ProbabilityWidget from "../components/ProbabilityWidget";
import WeatherCard from "../components/WeatherCard";
import PlayerPredictions from "../components/PlayerPredictions";
import CornerStats from "../components/CornerStats";
import { FirstHalfStats } from "../components/FirstHalfStats";
import { TeamAnalysis } from "../components/TeamAnalysis";
import PlayerStats from "../components/PlayerStats";
import { Paywall } from "../components/Account";
import { useAuth } from "../context/AuthContext";
import { TeamLogo, FormBadges } from "../components/TeamBits";
import { BestBets, Factors, ModelEvidence } from "../components/Insights";
import { RecentForm, H2HCard, StandingsCard, LiveEvents, NewsCard } from "../components/MatchContext";
import { ArrowLeft, RefreshCw, Loader2, Clock, Users, AlertTriangle, Crown } from "lucide-react";

function HeroAccount() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Link to="/login" className="text-sm text-[#A1A1AA] hover:text-white transition-colors" data-testid="hero-login-link">Accedi</Link>;
  return (
    <Link to="/premium" className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${user.is_premium ? "text-[#39FF14]" : "text-[#A1A1AA] hover:text-white"}`} data-testid="hero-premium-link">
      <Crown size={12} /> {user.is_premium ? "Premium" : "Passa a Premium"}
    </Link>
  );
}

function Hero({ match }) {
  const ko = new Date(match.kickoff);
  const place = [match.stadium.name, match.stadium.city].filter(Boolean).join(", ");
  const time = ko.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
  const date = ko.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", timeZone: "Europe/Rome" });
  return (
    <section className="relative overflow-hidden border-b border-[#27272A]">
      <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1676746610993-fa0c050d1f6d?crop=entropy&cs=srgb&fm=jpg&q=85')" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/80 to-[#050505]" />
      <div className="relative px-6 lg:px-10 py-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="back-link" className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors">
            <ArrowLeft size={14} strokeWidth={1.5} /> Tutte le partite
          </Link>
          <HeroAccount />
        </div>
        <div className="mt-6 flex items-center gap-3 text-xs">
          <span className="label">{match.league.name}</span>
          <span className="text-[#71717A]">·</span>
          <span className="text-[#A1A1AA]">{place}</span>
        </div>
        <div className="mt-8 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 lg:gap-10">
          <TeamBlock team={match.home} align="right" />
          <div className="text-center lg:min-w-[130px]" data-testid="match-score">
            {match.status === "live" && (
              <div className="flex items-center justify-center gap-1.5 text-[#FF3B30] font-black mb-2 mono">
                <span className="live-dot" /> {match.minute || match.status_detail}
              </div>
            )}
            {match.status === "upcoming" && (
              <div className="text-white mb-2 flex items-center justify-center gap-1.5 text-sm mono font-bold"><Clock size={13} strokeWidth={1.5} /> {time}</div>
            )}
            {match.status === "finished" && <div className="label text-[10px] mb-2">FINITA</div>}
            {match.score.home != null ? (
              <div className="heading text-4xl sm:text-6xl lg:text-7xl font-black mono tracking-tighter whitespace-nowrap">
                {match.score.home}<span className="text-[#3f3f46] mx-2">:</span>{match.score.away}
              </div>
            ) : (
              <div className="heading text-4xl lg:text-6xl font-black tracking-tighter text-[#A1A1AA]">VS</div>
            )}
            <div className="text-[11px] text-[#71717A] mt-3 capitalize">{date}</div>
          </div>
          <TeamBlock team={match.away} align="left" />
        </div>
      </div>
    </section>
  );
}

function TeamBlock({ team, align }) {
  const right = align === "right";
  return (
    <div className={`flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center ${right ? "sm:flex-row-reverse sm:text-right" : "sm:text-left"}`}>
      <div className="w-12 h-12 sm:w-[72px] sm:h-[72px] [&>img]:!w-full [&>img]:!h-full [&>div]:!w-full [&>div]:!h-full"><TeamLogo team={team} size={72} /></div>
      <div className="min-w-0 max-w-full">
        <h2 className="heading text-lg sm:text-2xl lg:text-4xl font-black tracking-tighter leading-none break-words">{team.name}</h2>
        <div className={`mt-2 hidden sm:flex ${right ? "justify-end" : ""}`}><FormBadges form={team.form} size="md" /></div>
      </div>
    </div>
  );
}

function PredictionStatus({ loading, error, onRetry }) {
  if (loading)
    return (
      <div className="surface-raised p-10 text-center" data-testid="prediction-loading">
        <Loader2 size={28} className="animate-spin mx-auto text-[#39FF14]" />
        <div className="heading text-xl font-black mt-4">AI al lavoro…</div>
        <p className="text-sm text-[#A1A1AA] mt-2">Incrocio di formazioni, quote, forma, scontri diretti, classifica, meteo e notizie (circa 30 secondi).</p>
      </div>
    );
  if (error)
    return (
      <div className="surface-raised p-8 text-center" data-testid="prediction-error">
        <AlertTriangle className="mx-auto text-[#FFCC00]" />
        <p className="text-sm text-[#A1A1AA] mt-3">Analisi AI non disponibile al momento.</p>
        <button onClick={onRetry} className="mt-4 px-4 py-2 border border-[#39FF14] text-[#39FF14] text-xs font-bold uppercase hover:bg-[#39FF14] hover:text-black transition-colors" data-testid="retry-prediction">Riprova</button>
      </div>
    );
  return null;
}

export default function MatchDetail() {
  const { id } = useParams();
  const { data: match, error: matchError } = useSWR(["match", id], () => fetchMatch(id), {
    refreshInterval: (m) => (m?.status === "live" ? 30000 : 0),
  });
  const [regenerating, setRegenerating] = useState(false);
  const { user } = useAuth();
  const premium = !!user?.is_premium;
  const { data: pred, error: predError, isLoading: predLoading, mutate } = useSWR(match && premium ? ["prediction", id] : null, () => fetchPrediction(id), {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
    refreshInterval: (d) => (d?.status === "generating" || d?.updating ? 4000 : 0),
  });

  useEffect(() => {
    if (match) document.title = `${match.home.name} vs ${match.away.name} — GOAL.AI`;
  }, [match]);

  if (matchError) return <div className="min-h-screen grid place-items-center text-[#A1A1AA]">Partita non trovata. <Link to="/" className="text-[#39FF14] ml-2">Torna indietro</Link></div>;
  if (!match) return <div className="min-h-screen grid place-items-center text-[#A1A1AA]"><Loader2 className="animate-spin" /></div>;

  const p = pred?.prediction;
  const updating = regenerating || !!pred?.updating;
  const regenerate = async () => {
    setRegenerating(true);
    try {
      await mutate(fetchPrediction(id, true), { revalidate: false });
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Hero match={match} />
      <section className="px-6 lg:px-10 py-10 max-w-7xl mx-auto space-y-6">
        {user !== null && !premium && <Paywall />}
        {premium && <PredictionStatus loading={predLoading || pred?.status === "generating"} error={predError && !p} onRetry={regenerate} />}

        {p && (
          <div className="surface-raised p-6 border-l-2 border-[#39FF14]" data-testid="ai-narrative">
            <div className="label mb-2">Analisi AI · GPT-6 Sol</div>
            <p className="text-base leading-relaxed">{p.narrative}</p>
            <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-3 border-t border-[#27272A] text-[11px] text-[#71717A]">
              <span className="mono">Aggiornata: {new Date(pred.generated_at).toLocaleString("it-IT")}</span>
              <button onClick={regenerate} disabled={updating} data-testid="refresh-prediction" className="flex items-center gap-1.5 text-[#007AFF] hover:text-[#39FF14] transition-colors disabled:opacity-50">
                <RefreshCw size={12} strokeWidth={1.5} className={updating ? "animate-spin" : ""} /> {updating ? "Aggiornamento… (~40s)" : "Rigenera con dati aggiornati"}
              </button>
            </div>
          </div>
        )}

        {p && <BestBets bets={p.best_bets} />}
        {p && <ModelEvidence model={p.model} />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {p && <ProbabilityWidget prediction={p} home={match.home.name} away={match.away.name} odds={match.odds} />}
            <TeamAnalysis match={match} />
            <CornerStats match={match} aiCorners={p?.corners} />
            <FirstHalfStats match={match} model={p?.model} />
            <div className="surface-raised p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="label flex items-center gap-2"><Users size={12} strokeWidth={1.5} /> Formazioni</div>
                <span data-testid="lineup-status" className={`label text-[10px] px-2 py-1 border ${match.lineup_confirmed ? "border-[#39FF14] text-[#39FF14]" : "border-[#FFCC00] text-[#FFCC00]"}`}>
                  {match.lineup_confirmed ? "Ufficiali" : "Probabili · ultima gara"}
                </span>
              </div>
              {match.home_formation || match.away_formation ? (
                <PitchFormation home={match.home_formation} away={match.away_formation} homeColor={match.home.color} awayColor={match.away.color} homeName={match.home.short} awayName={match.away.short} />
              ) : (
                <p className="text-sm text-[#A1A1AA]">Formazioni non ancora disponibili.</p>
              )}
            </div>
            <PlayerStats match={match} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <RecentForm team={match.home} side="home" />
              <RecentForm team={match.away} side="away" />
            </div>
          </div>

          <div className="space-y-6">
            <WeatherCard weather={match.weather} stadium={match.stadium} />
            <LiveEvents events={match.key_events} />
            {p && <PlayerPredictions players={p.key_players} homeName={match.home.short} awayName={match.away.short} stats={match.player_stats} />}
            {p && <Factors factors={p.factors} homeName={match.home.short} awayName={match.away.short} />}
            <StandingsCard standings={match.standings} />
            <H2HCard h2h={match.h2h} />
            <NewsCard news={match.news} />
          </div>
        </div>
      </section>
    </div>
  );
}
